/**
 * RealtimeAIWaiter Component
 * Real-time voice AI waiter using OpenAI Realtime API or VAPI
 * Provides ~300-800ms latency for natural conversations
 * 
 * Set VITE_USE_VAPI=true to use VAPI instead of OpenAI Realtime
 * VAPI offers ~60% cost savings and lower latency
 */

import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Mic, Volume2, Loader2, Wifi, WifiOff } from 'lucide-react';
import robotWaiterImage from '@/assets/lelah.gif';
import { useRealtimeAudio } from '@/hooks/useRealtimeAudio';
import { useVapiAudio } from '@/hooks/useVapiAudio';
import { useToast } from "@/hooks/use-toast";

// Feature flag: Set to true to use VAPI instead of OpenAI Realtime
const USE_VAPI = (import.meta as any).env.VITE_USE_VAPI === 'true';

interface RealtimeAIWaiterProps {
    restaurantId: string;
    restaurantName?: string;
    restaurantState?: string;
    restaurantDescription?: string;
    restaurantHours?: string;
    restaurantPhone?: string;
    restaurantAddress?: string;
    menuItems: any[];
    restaurantKnowledge?: {
        story?: string;
        philosophy?: string;
        sourcingPractices?: string;
        specialTechniques?: string;
        awards?: string;
        sustainabilityPractices?: string;
    } | null;
    faqKnowledge?: Array<{
        question: string;
        answer: string;
    }>;
    currentCart?: Array<{
        id: string;
        name: string;
        price: string;
        quantity: number;
        customerNote?: string;
        allergies?: string;
    }>;
    onAddToCart?: (item: any) => void;
    onRemoveFromCart?: (item: any) => void;
    onOrderConfirmed?: (order: any) => void;
    onCheckout?: (tipAmount?: number, customerNote?: string) => void;
    initiallyHidden?: boolean;
    tableId?: string;
    tableNumber?: string;
    customerSessionId?: string;
}

export interface RealtimeAIWaiterRef {
    triggerWelcome: () => Promise<void>;
    preConnect: () => Promise<void>;
}

export const RealtimeAIWaiter = forwardRef<RealtimeAIWaiterRef, RealtimeAIWaiterProps>(
    ({ restaurantId, restaurantName, restaurantState, restaurantDescription, restaurantHours, restaurantPhone, restaurantAddress, menuItems, restaurantKnowledge, faqKnowledge, currentCart, onAddToCart, onRemoveFromCart, onOrderConfirmed, onCheckout, initiallyHidden = false, tableId, tableNumber, customerSessionId }, ref) => {
        const { language } = useLanguage();
        const { toast } = useToast();

        const [isVisible, setIsVisible] = useState(true); // Always render, use CSS for visibility
        const [isActuallyVisible, setIsActuallyVisible] = useState(!initiallyHidden);
        const [isPressed] = useState(false);
        const [pendingStartListening, setPendingStartListening] = useState(false);
        const greetingAudioRef = useRef<HTMLAudioElement | null>(null);

        // Common callbacks for both implementations
        const commonCallbacks = {
            onAddToCart: (item: any) => {
                console.log('[RealtimeAIWaiter] Adding to cart:', item);
                onAddToCart?.(item);
            },
            onRemoveFromCart: (item: any) => {
                console.log('[RealtimeAIWaiter] Removing from cart:', item);
                onRemoveFromCart?.(item);
            },
            onOrderConfirmed: (order: any) => {
                console.log('[RealtimeAIWaiter] Order confirmed:', order);
                onOrderConfirmed?.(order);
            },
            onChefCalled: (message: string) => {
                toast({
                    title: "👨‍🍳 Kitchen Notified",
                    description: `Message sent to chef: "${message}"`,
                    duration: 5000,
                });
            },
            onWaiterCalled: (message: string) => {
                toast({
                    title: "🔔 Waiter Called",
                    description: `Waitstaff notified: "${message}"`,
                    duration: 5000,
                });
            },
            onCheckout: (tipAmount?: number, customerNote?: string) => {
                console.log('[RealtimeAIWaiter] Triggering checkout...', { tipAmount, customerNote });
                onCheckout?.(tipAmount, customerNote);
                toast({
                    title: "💳 Checkout",
                    description: tipAmount
                        ? `Opening payment page with $${tipAmount} tip...`
                        : "Opening secure payment page...",
                });
            },
            onTranscript: (text: string, role: 'user' | 'assistant') => {
                console.log(`[RealtimeAIWaiter] ${role}: ${text}`);
            },
            onError: (error: string) => {
                console.error('[RealtimeAIWaiter] Error:', error);
            },
            onStatusChange: (newStatus: string) => {
                console.log('[RealtimeAIWaiter] Status changed:', newStatus);
            },
        };

        // Use VAPI or OpenAI Realtime based on feature flag
        const vapiHook = useVapiAudio(USE_VAPI ? {
            publicKey: (import.meta as any).env.VITE_VAPI_PUBLIC_KEY || '',
            assistantId: (import.meta as any).env.VITE_VAPI_WAITER_ASSISTANT_ID || '',
            restaurantId,
            restaurantName,
            restaurantState,
            restaurantDescription,
            restaurantHours,
            restaurantPhone,
            restaurantAddress,
            tableId,
            tableNumber,
            customerSessionId,
            menuItems,
            restaurantKnowledge,
            faqKnowledge,
            currentCart,
            language,
            ...commonCallbacks,
        } : {
            // Minimal config when not using VAPI
            publicKey: '',
            assistantId: '',
            restaurantId: '',
        });

        const realtimeHook = useRealtimeAudio(!USE_VAPI ? {
            restaurantId,
            restaurantName,
            restaurantState,
            restaurantDescription,
            restaurantHours,
            restaurantPhone,
            restaurantAddress,
            tableId,
            tableNumber,
            customerSessionId,
            menuItems,
            restaurantKnowledge,
            faqKnowledge,
            currentCart,
            language,
            ...commonCallbacks,
        } : {
            // Minimal config when using VAPI
            restaurantId: '',
            tableId: '',
            menuItems: [],
        });

        // Select the active hook based on feature flag
        const {
            status,
            isSessionActive,
            connect,
            disconnect,
            initAudio,
            startListening,

        } = USE_VAPI ? vapiHook : realtimeHook;

        console.log(`[RealtimeAIWaiter] Using ${USE_VAPI ? 'VAPI' : 'OpenAI Realtime'} voice provider`);

        // Auto-start listening when connected if explicitly requested (e.g., via button click)
        useEffect(() => {
            if (pendingStartListening && status === 'connected') {
                console.log('[RealtimeAIWaiter] Connected! Starting listening as requested...');
                startListening();
                setPendingStartListening(false);
            }
        }, [status, pendingStartListening, startListening]);

        // Expose triggerWelcome method via ref
        useImperativeHandle(ref, () => ({
            triggerWelcome: async () => {
                setIsVisible(true);

                // 1. Initialize audio system (critical for browser autoplay rules)
                await initAudio();

                // 2. Play the high-quality pre-rendered greeting IMMEDIATELY
                // This provides zero-latency feedback to the user while VAPI/OpenAI connects
                let greetingPromise: Promise<void> | null = null;

                try {
                    // Use VITE_API_BASE_URL for the greeting endpoint
                    const backendBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';

                    if (backendBaseUrl) {
                        const greetingUrl = `${backendBaseUrl}/api/public/ai/greeting/${restaurantId}?lang=${language}`;
                        console.log('[RealtimeAIWaiter] Fetching instant greeting from:', greetingUrl);

                        // Fetch the audio first to check for errors
                        const response = await fetch(greetingUrl);
                        if (response.ok) {
                            // Get response as array buffer
                            let audioData: ArrayBuffer;
                            const arrayBuffer = await response.arrayBuffer();

                            // Check if the data looks like base64 (starts with common base64 chars)
                            // Lambda/API Gateway may return base64-encoded binary data
                            const firstBytes = new Uint8Array(arrayBuffer.slice(0, 20));
                            const firstChars = String.fromCharCode(...firstBytes);

                            if (firstChars.match(/^[A-Za-z0-9+/=]+$/)) {
                                // Data appears to be base64 encoded - decode it
                                console.log('[RealtimeAIWaiter] Detected base64 encoded audio, decoding...');
                                const base64String = new TextDecoder().decode(arrayBuffer);
                                const binaryString = atob(base64String);
                                const bytes = new Uint8Array(binaryString.length);
                                for (let i = 0; i < binaryString.length; i++) {
                                    bytes[i] = binaryString.charCodeAt(i);
                                }
                                audioData = bytes.buffer;
                            } else {
                                // Data is already binary
                                audioData = arrayBuffer;
                            }

                            // Create blob with explicit MIME type
                            const blob = new Blob([audioData], { type: 'audio/mpeg' });
                            const audioUrl = URL.createObjectURL(blob);
                            const welcomeAudio = new Audio(audioUrl);
                            greetingAudioRef.current = welcomeAudio;

                            console.log('[RealtimeAIWaiter] Playing instant greeting, blob size:', blob.size);

                            // Create a promise that resolves when the greeting ends
                            greetingPromise = new Promise<void>((resolve) => {
                                welcomeAudio.onended = () => {
                                    greetingAudioRef.current = null;
                                    URL.revokeObjectURL(audioUrl); // Clean up
                                    console.log('[RealtimeAIWaiter] Greeting finished');
                                    resolve();
                                };
                                // Also resolve after timeout in case audio fails
                                setTimeout(resolve, 15000); // Max 15 seconds
                            });

                            // Add error handler for audio loading issues
                            welcomeAudio.onerror = (e) => {
                                console.error('[RealtimeAIWaiter] Audio load error:', e);
                            };

                            welcomeAudio.play().catch(err => {
                                console.warn('[RealtimeAIWaiter] Audio play prevented:', err);
                            });
                        } else {
                            console.warn('[RealtimeAIWaiter] Greeting endpoint returned error status:', response.status);
                        }
                    } else {
                        console.warn('[RealtimeAIWaiter] No VITE_API_BASE_URL set, skipping greeting');
                    }
                } catch (err) {
                    console.error('[RealtimeAIWaiter] Failed to setup greeting audio:', err);
                }

                // 3. For VAPI: Wait for greeting to finish before connecting
                // This prevents audio conflicts with the Krisp noise processor
                if (USE_VAPI && greetingPromise) {
                    console.log('[RealtimeAIWaiter] Waiting for greeting to finish before connecting VAPI...');
                    await greetingPromise;
                }

                // 4. Connect to VAPI or OpenAI Realtime
                console.log(`[RealtimeAIWaiter] Connecting to ${USE_VAPI ? 'VAPI' : 'OpenAI Realtime'}...`);
                await connect();

                // NOTE: We do NOT start listening automatically.
                // User must explicitly click "Talk to Lelah" to enable the microphone.
                console.log('[RealtimeAIWaiter] Connected. Waiting for user to click Talk to Lelah.');
            },

            // Pre-connect without playing greeting (for when Lelah appears after greeting already played)
            preConnect: async () => {
                console.log('[RealtimeAIWaiter] Pre-connecting to OpenAI Realtime (no greeting)...');

                // Initialize audio context (needed for WebSocket audio streaming)
                await initAudio();

                // Connect to OpenAI Realtime
                await connect();

                console.log('[RealtimeAIWaiter] Pre-connected! Lelah is ready for instant response.');
            },

            setIsVisible: (visible: boolean) => {
                setIsActuallyVisible(visible);
            }
        }));

        // Update visibility when initiallyHidden changes
        useEffect(() => {
            setIsActuallyVisible(!initiallyHidden);
        }, [initiallyHidden]);

        // Handle click on the AI waiter button - acts as ON/OFF toggle
        const handleClick = useCallback(async () => {
            console.log('[RealtimeAIWaiter] Button clicked! Current status:', status);

            // Always stop greeting audio if playing
            if (greetingAudioRef.current) {
                console.log('[RealtimeAIWaiter] Stopping greeting audio');
                greetingAudioRef.current.pause();
                greetingAudioRef.current = null;
            }

            // If disconnected -> Turn ON (connect)
            if (status === 'disconnected') {
                console.log('[RealtimeAIWaiter] STATUS: disconnected -> Connecting...');
                setPendingStartListening(true);
                try {
                    await initAudio();
                    await connect();
                } catch (err) {
                    console.error('[RealtimeAIWaiter] Connection failed:', err);
                    setPendingStartListening(false);
                }
                return;
            }

            // If connecting -> Do nothing (wait for it to finish)
            if (status === 'connecting') {
                console.log('[RealtimeAIWaiter] STATUS: connecting -> Please wait...');
                return;
            }

            // If connected (Ready) -> Start talking immediately
            if (status === 'connected') {
                console.log('[RealtimeAIWaiter] STATUS: connected -> Starting listening...');
                startListening();
                return;
            }

            // If actively talking (listening, processing, speaking) -> Turn OFF (disconnect)
            console.log('[RealtimeAIWaiter] STATUS:', status, '-> Disconnecting...');
            disconnect();

        }, [status, connect, disconnect, initAudio]);

        // Get status text - simplified to 2 states
        const getStatusText = (): string => {
            switch (status) {
                case 'disconnected':
                    return 'Switch on Lelah';
                case 'connecting':
                    return 'Waking up Lelah...';
                case 'connected':
                    return 'Talk to Lelah';
                case 'listening':
                case 'processing':
                case 'speaking':
                    return 'Tap to Stop';
                default:
                    return '';
            }
        };

        // Get status icon
        const getStatusIcon = () => {
            switch (status) {
                case 'disconnected':
                    return <WifiOff className="w-6 h-6" />;
                case 'connecting':
                    return <Loader2 className="w-6 h-6 animate-spin" />;
                case 'connected':
                    return null; // Show robot image
                case 'listening':
                    return <Mic className="w-6 h-6" />;
                case 'processing':
                    return <Loader2 className="w-6 h-6 animate-spin" />;
                case 'speaking':
                    return <Volume2 className="w-6 h-6" style={{ animation: 'pulse 1s ease-in-out infinite' }} />;
                default:
                    return null;
            }
        };

        // Should show robot image? (when not actively showing a status icon)
        const showRobotImage = status === 'connected' || status === 'disconnected';

        // Is Leila actively TALKING? (listening, processing, or speaking - NOT just connected)
        // Connected = ready but waiting for user to tap (orange)
        // Actively talking = listening/processing/speaking (green)
        const isActivelyTalking = status === 'listening' || status === 'processing' || status === 'speaking';

        // Is Leila ready but waiting? (connected after greeting, before user taps)
        const isReadyToTalk = status === 'connected';

        return (
            <div
                className={`fixed bottom-6 right-6 z-50 flex flex-col items-center gap-3 transition-all duration-500 ${isActuallyVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none invisible'
                    }`}
                data-testid="realtime-ai-waiter"
            >

                {/* Main button */}
                <div
                    onClick={handleClick}
                    onTouchStart={(e) => e.preventDefault()} // Prevent ghost clicks
                    className={`
            relative w-[60px] h-[60px] rounded-full overflow-hidden
            flex items-center justify-center cursor-pointer shadow-2xl
            transition-all duration-200 ease-out
            ${showRobotImage
                            ? (isActivelyTalking
                                ? 'bg-gradient-to-br from-green-100 to-green-50 dark:from-green-900 dark:to-green-800 ring-2 ring-green-500/50'
                                : isReadyToTalk
                                    ? 'bg-gradient-to-br from-orange-100 to-orange-50 dark:from-orange-900 dark:to-orange-800 ring-2 ring-orange-500/50'
                                    : 'bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900 dark:to-blue-800')
                            : (isActivelyTalking
                                ? 'bg-gradient-to-br from-green-500 to-green-600'
                                : 'bg-gradient-to-br from-primary to-primary/80')
                        }
            ${status === 'listening' ? 'scale-110 shadow-[0_0_30px_rgba(34,197,94,0.6)]' : ''}
            ${status === 'speaking' ? 'scale-110 shadow-[0_0_30px_rgba(34,197,94,0.4)]' : ''}
            ${isPressed ? 'scale-95' : 'hover:scale-105'}
          `}
                    style={{
                        animation: status === 'listening' ? 'pulse 2s ease-in-out infinite' : undefined,
                    }}
                    data-testid="button-realtime-ai-waiter"
                >
                    {/* Pulse ring for active states */}
                    {(status === 'listening' || status === 'speaking') && (
                        <div
                            className={`absolute inset-0 rounded-full ${isActivelyTalking ? 'bg-green-500/20' : 'bg-primary/20'}`}
                            style={{
                                animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
                            }}
                        />
                    )}

                    {/* Robot image or status icon */}
                    {showRobotImage ? (
                        <img
                            src={robotWaiterImage}
                            alt="AI Waiter"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="relative z-10 text-white">
                            {getStatusIcon()}
                        </div>
                    )}

                    {/* Connection indicator */}
                    {isSessionActive && (
                        <div className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                    )}
                </div>

                {/* Status label */}
                <div
                    onClick={handleClick}
                    className={`
            ${isActivelyTalking
                            ? 'bg-green-500 text-white'
                            : isReadyToTalk
                                ? 'bg-orange-500 text-white'
                                : 'bg-primary text-primary-foreground'
                        } px-2 py-0.5 rounded-full 
            text-xs font-medium shadow-lg
            transition-all duration-200 cursor-pointer
            ${isPressed ? 'scale-95 shadow-md' : 'scale-100'}
          `}
                    style={{
                        animation: status === 'connected' && !isPressed ? 'bounce 2s ease-in-out infinite' : undefined,
                    }}
                    data-testid="text-realtime-status-label"
                >
                    {getStatusText()}
                </div>

                {/* Real-time indicator */}
                {isSessionActive && (
                    <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                        <Wifi className="w-3 h-3" />
                        <span>Real-time</span>
                    </div>
                )}
            </div>
        );
    }
);

RealtimeAIWaiter.displayName = 'RealtimeAIWaiter';
